import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

interface Props {
  /** Icon rendered in the header, before the title. */
  icon?: ReactNode;
  title: string;
  description?: string;
  open: boolean;
  onToggle: (next: boolean) => void;
  /** Rendered on the right of the header (e.g. a status chip). Clicks don't toggle. */
  headerAside?: ReactNode;
  children: ReactNode;
}

/**
 * Full-width collapsible settings block. The body is unmounted while collapsed,
 * so a section only fetches/renders once it is opened.
 *
 * An open section is marked in more than one way — accent edge, tinted header,
 * filled icon chip — so it reads as "the one you're working in" at a glance,
 * not by colour alone.
 */
export function AccordionSection({
  icon,
  title,
  description,
  open,
  onToggle,
  headerAside,
  children,
}: Props) {
  return (
    <section
      className={cn(
        "w-full overflow-visible rounded-xl border bg-white transition-[border-color,box-shadow] duration-200",
        open
          ? "border-bridge-accent/30 shadow-card"
          : "border-bridge-border hover:border-bridge-accent/20",
      )}
    >
      <div
        className={cn(
          "relative flex items-center gap-3 px-5 py-4 transition-colors",
          open ? "rounded-t-xl bg-bridge-accent-wash" : "rounded-xl",
        )}
      >
        {/* Accent edge on the open section's header. */}
        <span
          aria-hidden
          className={cn(
            "absolute inset-y-4 left-0 w-[3px] rounded-r-full transition-colors",
            open ? "bg-bridge-accent" : "bg-transparent",
          )}
        />
        <button
          type="button"
          aria-expanded={open}
          onClick={() => onToggle(!open)}
          className="flex flex-1 items-center gap-3 text-left"
        >
          {icon && (
            <span
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors",
                open ? "bg-bridge-accent/10 text-bridge-accent" : "bg-bridge-bg text-bridge-muted",
              )}
            >
              {icon}
            </span>
          )}
          <span className="min-w-0">
            <span className="block text-sm font-bold uppercase tracking-wider text-bridge-fg">
              {title}
            </span>
            {description && (
              <span className="mt-0.5 block text-xs text-bridge-muted">{description}</span>
            )}
          </span>
          <ChevronDown
            size={18}
            className={cn(
              "ml-auto shrink-0 transition-transform duration-200",
              open ? "rotate-180 text-bridge-fg" : "text-bridge-muted",
            )}
          />
        </button>
        {headerAside}
      </div>

      {/* No padding and no `overflow-hidden` here: the body owns its own spacing
          so a section can end in a full-bleed footer bar, and an overflow
          ancestor would stop that footer from sticking. */}
      {open && <div className="border-t border-bridge-accent/15">{children}</div>}
    </section>
  );
}
